import { useState, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { TablePagination } from "@/components/ui/table-pagination";
import {
  Download,
  FileText,
  Activity,
  ChevronDown,
  ChevronUp,
  Package,
  Plus,
  Minus,
  Check,
  AlertTriangle,
  CreditCard,
  MessageSquare,
  ExternalLink,
} from "lucide-react";
import { FaFileDownload, FaFilePdf } from "react-icons/fa";
import { format } from "date-fns";
import { Member } from "@/lib/settings/membersData";
import {
  useBillingData,
  InvoicesTableProps,
  BillingData,
  Invoice,
} from "@/lib/settings/billingsData";
import { Skeleton } from "@mui/material";
import {
  downloadBillingReport,
  downloadBillingReportForBundles,
  getInvoiceUrl,
} from "@/dispatchers/billingDispatchers";
import { ConsultationChargesModal } from "./ConsultationChargesModal";
import { useToast } from "@/hooks/use-toast";

const getBillingReportErrorMessage = async (report) => {
  const contentType = report.type?.toLowerCase() || "";

  if (contentType.includes("pdf")) {
    return null;
  }

  try {
    const text = (await report.text()).trim();
    if (!text.startsWith("{") && !text.startsWith("[")) {
      return null;
    }

    const payload = JSON.parse(text);
    return payload?.success === false && typeof payload?.message === "string"
      ? payload.message
      : null;
  } catch {
    return null;
  }
};

export function BillingSettingsTab() {
  const {
    darkMode: isDarkMode,
    data,
    members,
    billingTabs,
    activeTab,
    setActiveTab,
    getTextColor,
    invoicesPagination,
    companyInvoices,
    iscompanyInvoicesloading,
    companyId,
  } = useBillingData();
  const { toast } = useToast();

  const handleDownloadBundleBreakdown = useCallback(async () => {
    if (!companyId) return;

    const now = new Date();
    const report = await downloadBillingReportForBundles({
      companyId,
      BillinInvoiceType: 2,
    });

    if (!report) return;

    if (!(report instanceof Blob)) {
      const errorMessage =
        "errorMessage" in report ? report.errorMessage : null;
      if (errorMessage) {
        toast({
          title: "Bundle breakdown unavailable",
          description: errorMessage,
          variant: "destructive",
        });
      }
      return;
    }

    const blob = report;
    const errorMessage = await getBillingReportErrorMessage(blob);
    if (errorMessage) {
      toast({
        title: "Bundle breakdown unavailable",
        description: errorMessage,
        variant: "destructive",
      });
      return;
    }

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `bundle-breakdown-${format(now, "yyyy-MM")}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [companyId, toast]);

  return (
    <div className="p-6 space-y-6">
      <h2 className={`text-lg font-semibold ${getTextColor(isDarkMode)}`}>
        Billing & Usage
      </h2>

      {/* Tab Navigation */}
      <div
        className={`border-b ${
          isDarkMode ? "border-gray-700" : "border-gray-200"
        }`}
      >
        <div className="flex">
          {billingTabs.map((tab) => {
            const IconComponent = tab.icon;
            const isActive = activeTab === tab.id;

            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? isDarkMode
                      ? "text-white border-b-2 border-black bg-gray-700"
                      : "text-gray-900 border-b-2 border-black bg-gray-50"
                    : isDarkMode
                    ? "text-gray-400 hover:text-gray-200 hover:bg-gray-750"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                }`}
              >
                <IconComponent className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {activeTab === "current-period" && (
          <PlanBreakdown isDarkMode={isDarkMode} />
        )}
        {activeTab === "subscriptions" && (
          <SubscriptionsTab members={members} isDarkMode={isDarkMode} />
        )}
        {activeTab === "invoices" && (
          <div className="space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h3 className={`text-md font-medium ${getTextColor(isDarkMode)}`}>
                Previous Invoices
              </h3>
              <Button
                type="button"
                onClick={handleDownloadBundleBreakdown}
                disabled={!companyId}
                className="flex items-center justify-center gap-2 self-start sm:self-auto"
              >
                <Download className="w-4 h-4" />
                Bundle Breakdown
              </Button>
            </div>
            <InvoicesTable
              invoices={companyInvoices}
              isDarkMode={isDarkMode}
              currentPage={invoicesPagination.currentPage}
              itemsPerPage={invoicesPagination.itemsPerPage}
              onPageChange={invoicesPagination.handlePageChange}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function PlanBreakdown({ isDarkMode }: { isDarkMode?: boolean }) {
  const {
    getTextColor,
    getCardStyle,
    getSubCardStyle,
    isConsultationsExpanded,
    setIsConsultationsExpanded,
    isSmsExpanded,
    setIsSmsExpanded,
    isPromotionsExpanded,
    setIsPromotionsExpanded,
    companyServicesBilling,
    companySubscription,
    companyLedgerSummary,
    isCompanyLedgerSummaryloading,
    companyDetails,
    isConsultationsModalOpen,
    setIsConsultationsModalOpen,
    companySubscriptionCosts,
    companyPharmaciesCount,
  } = useBillingData();
  // Memoized calculations

  const userPackSummary = companyLedgerSummary?.items?.find(
    (x) => x.ledgerItemType === "UserPack",
  );

  const consultationItem = companyLedgerSummary?.items?.find(
    (x) => x.ledgerItemType === "Consultation",
  );

  const promotionalCredit = companyLedgerSummary?.items?.find(
    (x) => x.ledgerItemType === "PromotionalCredit",
  );

  const sms = companyLedgerSummary?.items?.find(
    (x) => x.ledgerItemType === "SMS",
  );

  const siteLicenseItem = companyLedgerSummary?.items?.find(
    (x) => x.ledgerItemType === "SiteLicense",
  );

  const bundlesItem = companyLedgerSummary?.items?.find(
    (x) => x.ledgerItemType === "Bundles",
  );

  const totalSiteLicensesPurchased =
    siteLicenseItem?.details?.reduce(
      (sum, d) => sum + (d?.licensesPurchased ?? 0),
      0,
    ) ?? 0;

  const totalSiteLicensePacks = totalSiteLicensesPurchased;
  const costPerSite = companySubscriptionCosts?.costPerSiteLicense || 0;
  const totalSiteLicenseCost = totalSiteLicensesPurchased * costPerSite;

  const totalPacksPurchased =
    userPackSummary?.details?.reduce(
      (sum, d) => sum + (d.packPurchased ?? 0),
      0,
    ) ?? 0;

  const currentMonthStart = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  }, []);

  const currentMonthEnd = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0);
  }, []);
  const activeSites = Number(companyPharmaciesCount) || 0;
  const calculations = useMemo(() => {
    const completedConsultations =
      companyServicesBilling?.completedConsultations ?? [];

    const completedCount = completedConsultations.length;
    const consultationCount = consultationItem?.count ?? 0;
    const totalSmsCharges =
      (sms?.count ?? 0) * (companySubscriptionCosts?.costPerSMS ?? 0);

    const totalSiteCost =
      siteLicenseItem?.details?.reduce(
        (sum, d) =>
          sum +
          Number(d?.licensesPurchased ?? 0) * Number(d?.ledgerItemCost ?? 0),
        0,
      ) ?? 0;

    // Calculate total pre-purchased consultations from Bundles + PromotionalCredit
    const bundlesPrePurchasedConsultations =
      bundlesItem?.details?.reduce(
        (sum, d) => sum + (d?.prePurchasedConsultation ?? 0),
        0,
      ) ?? 0;

    const promotionalPrePurchasedConsultations =
      promotionalCredit?.details?.reduce(
        (sum, d) => sum + (d?.prePurchasedConsultation ?? 0),
        0,
      ) ?? 0;

    const totalPrePurchasedConsultations =
      bundlesPrePurchasedConsultations + promotionalPrePurchasedConsultations;

    const chargeableConsultations = Math.max(
      0,
      consultationCount - totalPrePurchasedConsultations,
    );

    const totalConsultationFees =
      chargeableConsultations *
      (companySubscriptionCosts?.costPerConsultaion ?? 0);

    // Remaining = Total pre-purchased - completed
    const remainingPrePurchasedConsultations = Math.max(
      0,
      totalPrePurchasedConsultations - consultationCount,
    );

    const totalCredits = Number(companyLedgerSummary?.totalCredit ?? 0);
    const totalDebits = Number(companyLedgerSummary?.totalDebit ?? 0);
    const accountBalance = totalCredits - totalDebits;
    return {
      completedCount,
      completedConsultations,
      totalConsultationFees,
      totalSmsCharges,
      totalSiteCost,
      totalPrePurchasedConsultations,
      remainingPrePurchasedConsultations,
      totalDebits,
      accountBalance,
      totalSiteLicensesPurchased,
      costPerSite,
    };
  }, [
    companyServicesBilling,
    companyLedgerSummary,
    consultationItem,
    bundlesItem,
    promotionalCredit,
    companySubscriptionCosts,
    sms,
    totalSiteLicensesPurchased,
    costPerSite,
  ]);

  const toggleConsultationsExpansion = useCallback(() => {
    setIsConsultationsExpanded((prev) => !prev);
  }, []);

  const toggleSmsExpansion = useCallback(() => {
    setIsSmsExpanded((prev) => !prev);
  }, []);

  const togglePromotionsExpansion = useCallback(() => {
    setIsPromotionsExpanded((prev) => !prev);
  }, []);
  if (!companyLedgerSummary || isCompanyLedgerSummaryloading) {
    // Show skeleton for ledger summary
    const cardBg = isDarkMode ? "bg-gray-700" : "bg-gray-50";
    const subCardBg = isDarkMode ? "bg-gray-600" : "bg-white";
    return (
      <div className="p-6 space-y-6">
        {/* Title */}
        <Skeleton variant="text" width={200} height={30} />

        {/* Tabs */}
        <div className="flex gap-4">
          {Array.from({ length: 3 }).map((_, idx) => (
            <Skeleton
              key={idx}
              variant="rectangular"
              width={120}
              height={40}
              className={`${isDarkMode ? "bg-gray-600" : ""}`}
            />
          ))}
        </div>

        {/* Account Balance */}
        <div className={`${cardBg} p-6 rounded-lg space-y-4`}>
          <Skeleton variant="text" width={150} height={25} />
          <Skeleton variant="text" width={100} height={35} />
          <div className="grid grid-cols-2 gap-4">
            <Skeleton variant="text" width="80%" height={20} />
            <Skeleton variant="text" width="80%" height={20} />
          </div>
        </div>

        {/* Panels */}
        {[
          "Promotional Credits",
          "User Packs",
          "Consultation Costs",
          "SMS Usage",
        ].map((_, idx) => (
          <div key={idx} className={`${subCardBg} p-4 rounded-lg space-y-3`}>
            <Skeleton variant="text" width={200} height={25} />
            <Skeleton variant="text" width={150} height={20} />
            <Skeleton variant="rectangular" width="100%" height={60} />
            <Skeleton variant="text" width="80%" height={15} />
            <Skeleton variant="text" width="60%" height={15} />
          </div>
        ))}

        {/* Usage Metrics */}
        <div className={`${cardBg} p-6 rounded-lg space-y-4`}>
          <Skeleton variant="text" width={150} height={25} />
          <div className="grid grid-cols-1 gap-4 mt-4 md:grid-cols-3">
            {Array.from({ length: 3 }).map((_, idx) => (
              <Skeleton
                key={idx}
                variant="rectangular"
                width="100%"
                height={60}
                className="rounded-lg"
              />
            ))}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-6">
      {/* Account Balance Summary */}

      {/* User Packs Panel */}

      {/* {companyDetails?.companySubscriptionType !== 1 && (
      <div className={`${getSubCardStyle(isDarkMode)} p-4`}>
        <div className="flex items-center gap-3 mb-3">
          <Package className="w-5 h-5 text-blue-500" />
          <h4 className={`font-medium ${getTextColor(isDarkMode)}`}>
            User Packs
          </h4>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between">
            <span
              className={`text-sm ${getTextColor(isDarkMode, "secondary")}`}
            >
              Packs purchased:
            </span>
            <span className={`text-sm font-medium ${getTextColor(isDarkMode)}`}>
              {totalPacksPurchased}
            </span>
          </div>

          <div className="flex justify-between">
            <span
              className={`text-sm ${getTextColor(isDarkMode, "secondary")}`}
            >
              Total users:
            </span>
            <span className={`text-sm font-medium ${getTextColor(isDarkMode)}`}>
              {totalPacksPurchased * 10}
            </span>
          </div>

          <div className="flex justify-between">
            <span
              className={`text-sm ${getTextColor(isDarkMode, "secondary")}`}
            >
              Cost per pack:
            </span>
            <span className={`text-sm font-medium ${getTextColor(isDarkMode)}`}>
              £{userPackSummary?.totalLedgerItemCost ?? 0}
            </span>
          </div>

          <div className="pt-2 border-t border-gray-300 dark:border-gray-600">
            <div className="flex justify-between">
              <span className={`font-medium ${getTextColor(isDarkMode)}`}>
                Total cost:
              </span>
              <span
                className={`font-bold ${
                  isDarkMode ? "text-red-400" : "text-red-600"
                }`}
              >
                -£
                {totalPacksPurchased *
                  (userPackSummary?.totalLedgerItemCost ?? 0)}
              </span>
            </div>
          </div>
        </div>
      </div>
      )} */}

      {/* Site Packs Panel */}
      <div className={`${getSubCardStyle(isDarkMode)} p-4`}>
        <div className="flex items-center gap-3 mb-3">
          <Package className="w-5 h-5 text-blue-500" />
          <h4 className={`font-medium ${getTextColor(isDarkMode)}`}>
            Site Licences
          </h4>
        </div>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span
              className={`text-sm ${getTextColor(isDarkMode, "secondary")}`}
            >
              Site licences purchased:
            </span>
            <span className={`text-sm font-medium ${getTextColor(isDarkMode)}`}>
              {totalSiteLicensePacks}
            </span>
          </div>
          <div className="flex justify-between">
            <span
              className={`text-sm ${getTextColor(isDarkMode, "secondary")}`}
            >
              Total sites available:
            </span>
            <span className={`text-sm font-medium ${getTextColor(isDarkMode)}`}>
              {activeSites}
            </span>
          </div>
          <div className="flex justify-between">
            <span
              className={`text-sm ${getTextColor(isDarkMode, "secondary")}`}
            >
              Cost per site:
            </span>
            <span className={`text-sm font-medium ${getTextColor(isDarkMode)}`}>
              £{Number(costPerSite.toFixed(3))}
            </span>
          </div>
          <div className="pt-2 border-t border-gray-300 dark:border-gray-600">
            <div className="flex justify-between">
              <span className={`font-medium ${getTextColor(isDarkMode)}`}>
                Total cost:
              </span>
              <span
                className={`font-bold ${
                  isDarkMode ? "text-red-400" : "text-red-600"
                }`}
              >
                -£{Number(calculations.totalSiteCost.toFixed(3))}
              </span>
            </div>
          </div>
        </div>
      </div>
      {/* Consultation Costs Panel */}
      <div className={`${getSubCardStyle(isDarkMode)} p-4`}>
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={toggleConsultationsExpansion}
        >
          <div className="flex items-center gap-3">
            <Activity className="w-5 h-5 text-purple-500" />
            <h4 className={`font-medium ${getTextColor(isDarkMode)}`}>
              Consultation Costs
            </h4>
          </div>
          {isConsultationsExpanded ? (
            <ChevronUp className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          )}
        </div>

        <div className="mt-3 space-y-2">
          <div className="flex justify-between">
            <span
              className={`text-sm ${getTextColor(isDarkMode, "secondary")}`}
            >
              Consultations completed:
            </span>
            <span className={`text-sm font-medium ${getTextColor(isDarkMode)}`}>
              {consultationItem?.count}
            </span>
          </div>

          <div className="flex justify-between">
            <span
              className={`text-sm ${getTextColor(isDarkMode, "secondary")}`}
            >
              Pre-Purchased Consultations:
            </span>
            <span className={`text-sm font-medium ${getTextColor(isDarkMode)}`}>
              {calculations.remainingPrePurchasedConsultations}
            </span>
          </div>

          <div className="pt-2 border-t border-gray-300 dark:border-gray-600">
            <div className="flex justify-between">
              <span className={`font-medium ${getTextColor(isDarkMode)}`}>
                Total consultation costs:
              </span>
              <span
                className={`font-bold ${
                  isDarkMode ? "text-red-400" : "text-red-600"
                }`}
              >
                -£{Number(calculations.totalConsultationFees.toFixed(3))}
              </span>
            </div>
          </div>
        </div>
        {/* Link to open detailed breakdown modal */}
        <div className="pt-3 border-t border-gray-300 dark:border-gray-600">
          <button
            onClick={() => setIsConsultationsModalOpen(true)}
            className={`flex items-center gap-2 text-sm font-medium transition-colors ${
              isDarkMode
                ? "text-blue-400 hover:text-blue-300"
                : "text-blue-600 hover:text-blue-700"
            }`}
          >
            <ExternalLink className="w-4 h-4" />
            View full consultation breakdown
          </button>
        </div>

        {/* Consultation Charges Modal */}
        <ConsultationChargesModal
          isOpen={isConsultationsModalOpen}
          onOpenChange={setIsConsultationsModalOpen}
          consultations={consultationItem}
          consultationFee={consultationItem?.totalLedgerItemCost}
          isDarkMode={isDarkMode}
        />
      </div>
      {/* SMS Usage Panel */}
      <div className={`${getSubCardStyle(isDarkMode)} p-4`}>
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={toggleSmsExpansion}
        >
          <div className="flex items-center gap-3">
            <MessageSquare className="w-5 h-5 text-blue-500" />
            <h4 className={`font-medium ${getTextColor(isDarkMode)}`}>
              SMS Usage
            </h4>
          </div>
          {isSmsExpanded ? (
            <ChevronUp className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          )}
        </div>

        <div className="mt-3 space-y-2">
          <div className="flex justify-between">
            <span
              className={`text-sm ${getTextColor(isDarkMode, "secondary")}`}
            >
              SMS messages sent:
            </span>
            <span className={`text-sm font-medium ${getTextColor(isDarkMode)}`}>
              {sms?.count ?? 0}
            </span>
          </div>
          <div className="flex justify-between">
            <span
              className={`text-sm ${getTextColor(isDarkMode, "secondary")}`}
            >
              Cost per SMS:
            </span>
            <span className={`text-sm font-medium ${getTextColor(isDarkMode)}`}>
              £{sms?.totalLedgerItemCost ?? 0}
            </span>
          </div>
          <div className="pt-2 border-t border-gray-300 dark:border-gray-600">
            <div className="flex justify-between">
              <span className={`font-medium ${getTextColor(isDarkMode)}`}>
                Total SMS costs:
              </span>
              <span
                className={`font-bold ${
                  isDarkMode ? "text-red-400" : "text-red-600"
                }`}
              >
                -£
                {Number(
                  ((sms?.count ?? 0) * (sms?.totalLedgerItemCost ?? 0)).toFixed(
                    3,
                  ),
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Expandable SMS List */}
        {isSmsExpanded && sms?.details?.length > 0 && (
          <div className="pt-4 mt-4 border-t border-gray-300 dark:border-gray-600">
            <h5
              className={`text-sm font-medium mb-3 ${getTextColor(isDarkMode)}`}
            >
              SMS Details
            </h5>
            <div className="space-y-2 overflow-y-auto max-h-48">
              {sms?.details?.map((item, index) => (
                <div
                  key={index}
                  className={`flex justify-between items-start p-2 rounded text-xs ${
                    isDarkMode
                      ? "bg-gray-600 text-gray-200"
                      : "bg-white text-gray-700"
                  }`}
                >
                  <div className="flex-1">
                    <div className="font-medium">{item.createdByName}</div>
                    <div className="text-xs opacity-75">{item.phoneNumber}</div>
                    <div className="mt-1 text-xs opacity-90">
                      {item.sms.length > 60
                        ? `${item?.sms?.substring(0, 60)}...`
                        : item?.sms}
                    </div>
                  </div>
                  <div className="ml-2 font-medium">
                    £{sms?.totalLedgerItemCost}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Next Invoice Preview */}
      <div className={`${getCardStyle(isDarkMode)} p-6`}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className={`text-lg font-semibold ${getTextColor(isDarkMode)}`}>
              Next Invoice (so far)
            </h4>
            <div
              className={`text-xs ${getTextColor(
                isDarkMode,
                "secondary",
              )} mt-1`}
            >
              Estimated based on usage this billing period · due{" "}
              {format(currentMonthEnd, "MMM dd, yyyy")}
            </div>
          </div>
          <span className={`text-2xl font-bold ${getTextColor(isDarkMode)}`}>
            £
            {Number(
              (
                calculations.totalSiteCost +
                calculations.totalConsultationFees +
                calculations.totalSmsCharges
              ).toFixed(3),
            )}
          </span>
        </div>
        <div className="space-y-2 pt-4 border-t border-gray-300 dark:border-gray-600">
          <div className="flex justify-between">
            <span
              className={`text-sm ${getTextColor(isDarkMode, "secondary")}`}
            >
              Site licences
            </span>
            <span className={`text-sm font-medium ${getTextColor(isDarkMode)}`}>
              £{Number(calculations.totalSiteCost.toFixed(3))}
            </span>
          </div>
          <div className="flex justify-between">
            <span
              className={`text-sm ${getTextColor(isDarkMode, "secondary")}`}
            >
              Consultation costs
            </span>
            <span className={`text-sm font-medium ${getTextColor(isDarkMode)}`}>
              £{Number(calculations.totalConsultationFees.toFixed(3))}
            </span>
          </div>
          <div className="flex justify-between">
            <span
              className={`text-sm ${getTextColor(isDarkMode, "secondary")}`}
            >
              SMS usage
            </span>
            <span className={`text-sm font-medium ${getTextColor(isDarkMode)}`}>
              £{Number(calculations.totalSmsCharges.toFixed(3))}
            </span>
          </div>
          <div className="pt-2 border-t border-gray-300 dark:border-gray-600 flex justify-between">
            <span className={`font-medium ${getTextColor(isDarkMode)}`}>
              Estimated total
            </span>
            <span className={`font-bold ${getTextColor(isDarkMode)}`}>
              £
              {Number(
                (
                  calculations.totalSiteCost +
                  calculations.totalConsultationFees +
                  calculations.totalSmsCharges
                ).toFixed(3),
              )}
            </span>
          </div>
        </div>
      </div>

      {/* Promotional Credits Panel */}
      {/* <div className={`${getSubCardStyle(isDarkMode)} p-4`}>
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={togglePromotionsExpansion}
        >
          <div className="flex items-center gap-3">
            <CreditCard className="w-5 h-5 text-green-500" />
            <h4 className={`font-medium ${getTextColor(isDarkMode)}`}>
              Promotional Credits
            </h4>
          </div>
          {isPromotionsExpanded ? (
            <ChevronUp className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          )}
        </div>

        <div className="mt-3 space-y-2">
          <div className="flex justify-between">
            <span
              className={`text-sm ${getTextColor(isDarkMode, "secondary")}`}
            >
              Credits applied:
            </span>
            <span className={`text-sm font-medium ${getTextColor(isDarkMode)}`}>
              {promotionalCredit?.count}
            </span>
          </div>
          <div className="pt-2 border-t border-gray-300 dark:border-gray-600">
            <div className="flex justify-between">
              <span className={`font-medium ${getTextColor(isDarkMode)}`}>
                Total promotional credits:
              </span>
              <span
                className={`font-bold ${
                  isDarkMode ? "text-green-400" : "text-green-600"
                }`}
              >
                £{promotionalCredit?.totalLedgerItemCost.toFixed(3)}
              </span>
            </div>
          </div>
        </div>

        {/* Expandable Promotions List */}
      {/* isPromotionsExpanded && (
          <div className="pt-4 mt-4 border-t border-gray-300 dark:border-gray-600">
            <h5
              className={`text-sm font-medium mb-3 ${getTextColor(isDarkMode)}`}
            >
              Credit History
            </h5>

            <div className="space-y-2 overflow-y-auto max-h-48">
              {promotionalCredit?.details?.map((credit, index) => (
                <div
                  key={index}
                  className={`flex justify-between items-start p-2 rounded text-xs ${
                    isDarkMode
                      ? "bg-gray-600 text-gray-200"
                      : "bg-white text-gray-700"
                  }`}
                >
                  <div className="flex-1">
                    {/* Promotion Title */}
      {/* <div className="font-medium">{credit.promotionTitle}</div>

                    {/* CreatedBy + Date */}
      {/* <div className="text-xs opacity-75">
                      Applied by {credit.createdBy} •{" "}
                      {format(new Date(credit.createdAt), "MMM dd, yyyy")}
                    </div>

                    {/* Notes (if exist) */}
      {/* {credit.promotionNotes && (
                      <div className="mt-1 text-xs opacity-90">
                        {credit.promotionNotes}
                      </div>
                    )} */}
      {/* </div>

                  {/* Credit Amount */}
      {/* <div
                    className={`font-medium ml-2 ${
                      isDarkMode ? "text-green-300" : "text-green-600"
                    }`}
                  >
                    +£{Number(credit?.creditAmount).toFixed(3)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) */}
      {/* </div> */}
    </div>
  );
}
function SubscriptionsTab({
  members,
  isDarkMode,
}: {
  members?: Member[];
  isDarkMode?: boolean;
}) {
  const {
    getTextColor,
    getCardStyle,
    showConfirmDialog,
    setShowConfirmDialog,
    companyLedgerSummary,
    isCompanyLedgerSummaryloading,
    companySubscription,
    CompanySubscriptionType,
    handleConfirmChanges,
    companyUserCount,
    companyDetails,
    updateUserPackLoading,
    companyPharmaciesCount,
    companySubscriptionCosts,
  } = useBillingData();

  // Calculate site licenses from ledger (same as PlanBreakdown)
  const siteLicenseItem = companyLedgerSummary?.items?.find(
    (x) => x.ledgerItemType === "SiteLicense",
  );

  const totalSiteLicensesPurchased =
    siteLicenseItem?.details?.reduce(
      (sum, d) => sum + (d?.licensesPurchased ?? 0),
      0,
    ) ?? 0;

  // Flat-rate pricing model: £25 per site
  const initialPacks = totalSiteLicensesPurchased;
  const [packs, setPacks] = useState<number>(initialPacks);
  const hasChanges = packs !== initialPacks;
  const activeSites = Number(companyPharmaciesCount) || 0;
  const costPerSite = companySubscriptionCosts?.costPerSiteLicense || 25;

  // Calculate cost: flat rate per site
  const calculateCost = (packCount: number) => {
    return packCount * costPerSite;
  };
  const currentMonthEnd = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0);
  }, []);
  const metrics = useMemo(() => {
    const usersPerPack = companySubscription?.totalUserPacks;
    const currentActiveUsers = companyUserCount;
    const totalCapacity = 10 * companySubscription?.totalUserPacks;
    const available = Math.max(0, totalCapacity - currentActiveUsers);
    const minPacksNeeded = Math.ceil(currentActiveUsers / 10) || 0;
    const belowCapacity = packs < minPacksNeeded;
    // Calculate basePlanCost from ledger: multiply licensesPurchased by netLedgerItemCost for each detail
    const basePlanCost =
      siteLicenseItem?.details?.reduce(
        (total, detail) =>
          total +
          (detail?.licensesPurchased ?? 0) * (detail?.netLedgerItemCost ?? 0),
        0,
      ) ?? 0;

    return {
      usersPerPack,
      currentActiveUsers,
      totalCapacity,
      available,
      minPacksNeeded,
      belowCapacity,
      basePlanCost,
    };
  }, [
    packs,
    companySubscription,
    initialPacks,
    companyUserCount,
    costPerSite,
    companyLedgerSummary,
    siteLicenseItem,
  ]);

  const increment = useCallback(() => setPacks((p) => p + 1), []);
  const decrement = useCallback(() => {
    setPacks((p) => {
      const newPacks = p - 1;
      return Math.max(metrics.minPacksNeeded, newPacks);
    });
  }, [metrics.minPacksNeeded]);

  // Calculate costs - incremental billing (only charge for additional licenses)
  const oldCost = calculateCost(initialPacks);
  const newCost = calculateCost(packs);
  const costDifference = newCost - oldCost;
  const isIncrease = costDifference > 0;
  const additionalLicenses = packs - initialPacks;
  const additionalLicenseCost = additionalLicenses * costPerSite;

  // Get subscription type name
  const getSubscriptionTypeName = () => {
    const type = companySubscription?.companySubscriptionType;
    return CompanySubscriptionType[type] ?? "Standard";
  };

  const safeFormatDate = (dateValue: string | Date | null | undefined) => {
    if (!dateValue) return "";
    const d = new Date(dateValue);
    if (isNaN(d.getTime())) return "";
    return format(d, "MMM dd, yyyy");
  };

  return (
    <div className="space-y-6">
      {/* Subscription Section */}
      <div className={`${getCardStyle(isDarkMode)} p-6`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-lg font-semibold ${getTextColor(isDarkMode)}`}>
            Subscription
          </h3>
          <div className={`text-lg font-semibold ${getTextColor(isDarkMode)}`}>
            £{Number(metrics.basePlanCost.toFixed(3))}
          </div>
        </div>

        <div className="space-y-4">
          {/* Subscription Type */}
          {/* <div className="flex items-center justify-between">
            <div>
              <span className={`font-medium ${getTextColor(isDarkMode)}`}>
                {getSubscriptionTypeName()}
              </span>
            </div>
            <Badge
              variant="outline"
              className={`${
                isDarkMode
                  ? "border-purple-600 text-purple-400"
                  : "border-purple-600 text-purple-600"
              }`}
            >
              {getSubscriptionTypeName()}
            </Badge>
          </div> */}

          {/* Contract Dates */}
          {/* <div
            className={`pt-3 border-t ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span
                  className={`text-sm ${getTextColor(isDarkMode, "secondary")}`}
                >
                  Contract Start Date:
                </span>
                <span
                  className={`text-sm font-medium ${getTextColor(isDarkMode)}`}
                >
                  {safeFormatDate(companySubscription?.startDate)}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span
                  className={`text-sm ${getTextColor(isDarkMode, "secondary")}`}
                >
                  Contract End Date:
                </span>
                <span
                  className={`text-sm font-medium ${getTextColor(isDarkMode)}`}
                >
                  {safeFormatDate(companySubscription?.endDate)}
                </span>
              </div>
            </div>
          </div> */}
          <div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span
                  className={`text-sm ${getTextColor(isDarkMode, "secondary")}`}
                >
                  Next Invoice Date:
                </span>
                <span
                  className={`text-sm font-medium ${getTextColor(isDarkMode)}`}
                >
                  {companySubscription?.nextUserPackInvoiceDate
                    ? format(
                        new Date(companySubscription.nextUserPackInvoiceDate),
                        "MMM dd, yyyy",
                      )
                    : format(currentMonthEnd, "MMM dd, yyyy")}
                </span>
              </div>
            </div>
          </div>
          {/* User Packs Section */}
          <div className="flex items-center justify-between py-3 border-t border-gray-200 dark:border-gray-700">
            <div className="flex-1">
              <div className={`font-medium ${getTextColor(isDarkMode)}`}>
                Site licences
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={decrement}
                  disabled={packs <= initialPacks}
                  className={`${
                    isDarkMode
                      ? "border-gray-600 text-white h-8 w-8 p-0"
                      : "h-8 w-8 p-0"
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                  title={
                    packs <= metrics.minPacksNeeded
                      ? `Cannot reduce below ${metrics.minPacksNeeded} packs (${metrics.currentActiveUsers} active members require ${metrics.minPacksNeeded} packs)`
                      : ""
                  }
                >
                  <Minus className="w-3 h-3" />
                </Button>
                <span
                  className={`px-3 py-1 rounded ${
                    isDarkMode ? "bg-gray-700" : "bg-gray-100"
                  } ${getTextColor(isDarkMode)} min-w-[60px] text-center`}
                >
                  {packs}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={increment}
                  className={
                    isDarkMode
                      ? "border-gray-600 text-white h-8 w-8 p-0"
                      : "h-8 w-8 p-0"
                  }
                >
                  <Plus className="w-3 h-3" />
                </Button>
                <span
                  className={`text-sm ${getTextColor(
                    isDarkMode,
                    "secondary",
                  )} ml-2`}
                >
                  ({packs} site
                  {packs !== 1 ? "s" : ""} • unlimited users)
                </span>
              </div>

              {packs <= metrics.minPacksNeeded &&
                metrics.currentActiveUsers > 0 && (
                  <div
                    className={`mt-2 p-2 rounded text-xs ${
                      isDarkMode
                        ? "bg-yellow-900/30 text-yellow-300 border border-yellow-700"
                        : "bg-yellow-50 text-yellow-800 border border-yellow-200"
                    }`}
                  >
                    ⚠️ You have {activeSites} active site
                    {activeSites !== 1 ? "s" : ""} and need at least{" "}
                    {activeSites} site licence
                    {activeSites !== 1 ? "s" : ""}. Deactivate sites to reduce
                    licences.
                  </div>
                )}
            </div>
            <div className={`font-medium ${getTextColor(isDarkMode)}`}>
              {additionalLicenses > 0
                ? `£${Number(additionalLicenseCost.toFixed(3))}`
                : "No change"}
            </div>
          </div>

          {metrics.belowCapacity && (
            <div
              className={`p-3 rounded-lg border ${
                isDarkMode
                  ? "border-red-600 bg-red-900/20 text-red-300"
                  : "border-red-300 bg-red-50 text-red-800"
              }`}
            >
              <p className="text-sm font-medium mb-1">
                ⚠️ Insufficient Site Capacity
              </p>
              <p className="text-sm">
                You currently have {activeSites} active site
                {activeSites !== 1 ? "s" : ""} but only {packs} site licence
                {packs !== 1 ? "s" : ""}. You need to add at least{" "}
                {metrics.minPacksNeeded - packs} more site licence
                {metrics.minPacksNeeded - packs !== 1 ? "s" : ""} or deactivate{" "}
                {activeSites - packs} site
                {activeSites - packs !== 1 ? "s" : ""}.
              </p>
            </div>
          )}
        </div>

        {hasChanges && (
          <div className="flex items-center justify-between pt-4 mt-6 border-t border-gray-200 dark:border-gray-700">
            <div className={`text-sm ${getTextColor(isDarkMode, "secondary")}`}>
              You have unsaved changes to your subscription.
            </div>
            <Button
              onClick={() => setShowConfirmDialog(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              disabled={additionalLicenses <= 0}
            >
              <Check className="w-4 h-4 mr-2" />
              Confirm Changes
            </Button>
          </div>
        )}

        <AlertDialog
          open={showConfirmDialog}
          onOpenChange={setShowConfirmDialog}
        >
          <AlertDialogContent
            className={isDarkMode ? "bg-gray-800 border-gray-700" : ""}
          >
            <AlertDialogHeader>
              <div className="flex items-center gap-2">
                <AlertTriangle
                  className={`w-5 h-5 ${
                    isIncrease ? "text-orange-500" : "text-blue-500"
                  }`}
                />
                <AlertDialogTitle className={getTextColor(isDarkMode)}>
                  Confirm Billing Changes
                </AlertDialogTitle>
              </div>
              <AlertDialogDescription
                className={getTextColor(isDarkMode, "secondary")}
              >
                Your subscription billing will change immediately.
              </AlertDialogDescription>
            </AlertDialogHeader>

            <div
              className={`space-y-4 py-4 ${
                isDarkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className={getTextColor(isDarkMode, "secondary")}>
                    Current site licences:
                  </span>
                  <span className={`font-semibold ${getTextColor(isDarkMode)}`}>
                    {initialPacks}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className={getTextColor(isDarkMode, "secondary")}>
                    New site licences:
                  </span>
                  <span className={`font-semibold ${getTextColor(isDarkMode)}`}>
                    {packs}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className={getTextColor(isDarkMode, "secondary")}>
                    Additional licences:
                  </span>
                  <span className={`font-semibold ${getTextColor(isDarkMode)}`}>
                    {additionalLicenses}
                  </span>
                </div>
              </div>

              <div
                className={`border-t ${
                  isDarkMode ? "border-gray-700" : "border-gray-200"
                } pt-4 space-y-2`}
              >
                <div className="flex items-center justify-between">
                  <span className={getTextColor(isDarkMode, "secondary")}>
                    Cost per licence:
                  </span>
                  <span className={getTextColor(isDarkMode)}>
                    £{Number(costPerSite.toFixed(3))}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className={getTextColor(isDarkMode, "secondary")}>
                    Charge for additional licences:
                  </span>
                  <span className={`font-semibold ${getTextColor(isDarkMode)}`}>
                    £{Number(additionalLicenseCost.toFixed(3))}
                  </span>
                </div>
              </div>
              <div
                className={`${
                  isDarkMode
                    ? "bg-blue-900/20 border-blue-600"
                    : "bg-blue-50 border-blue-200"
                } border rounded-lg p-3`}
              >
                <p
                  className={`text-sm ${
                    isDarkMode ? "text-blue-200" : "text-blue-800"
                  }`}
                >
                  💳 You will be charged £
                  {Number(additionalLicenseCost.toFixed(3))} pro-rated for{" "}
                  {additionalLicenses} new licence
                  {additionalLicenses !== 1 ? "s" : ""} for the remainder of
                  this billing period.
                </p>
              </div>
            </div>

            <AlertDialogFooter>
              <AlertDialogCancel
                className={
                  isDarkMode
                    ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                    : ""
                }
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={async (e) => {
                  e.preventDefault();
                  await handleConfirmChanges(packs);
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                disabled={updateUserPackLoading}
              >
                {updateUserPackLoading
                  ? "Please wait..."
                  : "Confirm & Update Billing"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {/* Usage Summary */}
      {/* <div className={`${getCardStyle(isDarkMode)} p-6`}>
        <h4 className={`font-medium ${getTextColor(isDarkMode)} mb-4`}>
          Current Usage
        </h4>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <MetricCard
            title="Active Users"
            value={metrics.currentActiveUsers}
            isDarkMode={isDarkMode}
          />
          <MetricCard
            title="Available Slots"
            value={metrics.available}
            isDarkMode={isDarkMode}
            colorClass={isDarkMode ? "text-green-300" : "text-green-700"}
          />
          <MetricCard
            title="Utilization"
            value={`${Math.round(
              (metrics.currentActiveUsers / metrics.totalCapacity) * 100,
            )}%`}
            isDarkMode={isDarkMode}
            colorClass={isDarkMode ? "text-blue-300" : "text-blue-700"}
          />
        </div>
      </div> */}
    </div>
  );
}
// function SubscriptionsTab({
//   members,
//   isDarkMode,
// }: {
//   members?: Member[];
//   isDarkMode?: boolean;
// }) {
//   const {
//     getTextColor,
//     getCardStyle,
//     showConfirmDialog,
//     setShowConfirmDialog,
//     companyLedgerSummary,
//     isCompanyLedgerSummaryloading,
//     companySubscription,
//     CompanySubscriptionType,
//     handleConfirmChanges,
//     companyUserCount,
//     companyDetails,
//     updateUserPackLoading,
//   } = useBillingData();
//   // Memoized calculations
//   const initialPacks = companySubscription?.totalUserPacks ?? 10;
//   const [packs, setPacks] = useState<number>(initialPacks);
//   const hasChanges = packs !== initialPacks;

//   const metrics = useMemo(() => {
//     const usersPerPack = companySubscription?.totalUserPacks;
//     const currentActiveUsers = companyUserCount;
//     const totalCapacity = 10 * companySubscription?.totalUserPacks;
//     const available = Math.max(0, totalCapacity - currentActiveUsers);
//     const minPacksNeeded = Math.ceil(currentActiveUsers / 10) || 0;
//     const belowCapacity = packs < minPacksNeeded;
//     const basePlanCost =
//       companySubscription?.costPerPack * companySubscription?.totalUserPacks;
//     return {
//       usersPerPack,
//       currentActiveUsers,
//       totalCapacity,
//       available,
//       minPacksNeeded,
//       belowCapacity,
//       basePlanCost,
//     };
//   }, [packs, members, companySubscription]);

//   const increment = useCallback(() => setPacks((p) => p + 1), []);
//   const decrement = useCallback(() => {
//     setPacks((p) => {
//       const newPacks = p - 1;
//       return Math.max(metrics.minPacksNeeded, newPacks);
//     });
//   }, [metrics.minPacksNeeded]);

//   const oldCost = initialPacks * companySubscription?.costPerPack;
//   const newCost = packs * companySubscription?.costPerPack;
//   const costDifference = newCost - oldCost;
//   const isIncrease = costDifference > 0;

//   const safeFormatDate = (dateValue: string | Date | null | undefined) => {
//     if (!dateValue) return "";

//     const d = new Date(dateValue);
//     if (isNaN(d.getTime())) return "";

//     return format(d, "MMM dd, yyyy");
//   };

//   return (
//     <div className="space-y-6">
//       {/* Subscription Section */}
//       <div className={`${getCardStyle(isDarkMode)} p-6`}>
//         <div className="flex items-center justify-between mb-4">
//           <h3 className={`text-lg font-semibold ${getTextColor(isDarkMode)}`}>
//             Subscription
//           </h3>
//           <div className={`text-lg font-semibold ${getTextColor(isDarkMode)}`}>
//             £{metrics.basePlanCost.toFixed(2)}
//           </div>
//         </div>

//         <div className="space-y-4">
//           <div className="flex items-center justify-between">
//             <div>
//               <span className={`font-medium ${getTextColor(isDarkMode)}`}>
//                 {CompanySubscriptionType[
//                   companyDetails?.companySubscriptionType
//                 ] ?? "Unknown"}
//               </span>
//             </div>
//             <Badge
//               variant="outline"
//               className={`${
//                 isDarkMode
//                   ? "border-purple-600 text-purple-400"
//                   : "border-purple-600 text-purple-600"
//               }`}
//             >
//               {CompanySubscriptionType[
//                 companyDetails?.companySubscriptionType
//               ] ?? "Unknown"}
//             </Badge>
//           </div>

//           <div
//             className={`pt-3 border-t ${
//               isDarkMode ? "border-gray-700" : "border-gray-200"
//             }`}
//           >
//             <div className="space-y-2">
//               <div className="flex items-center justify-between">
//                 <span
//                   className={`text-sm ${getTextColor(isDarkMode, "secondary")}`}
//                 >
//                   Contract Start Date:
//                 </span>
//                 <span
//                   className={`text-sm font-medium ${getTextColor(isDarkMode)}`}
//                 >
//                   {safeFormatDate(companySubscription?.startDate)}
//                 </span>
//               </div>

//               <div className="flex items-center justify-between">
//                 <span
//                   className={`text-sm ${getTextColor(isDarkMode, "secondary")}`}
//                 >
//                   Contract End Date:
//                 </span>
//                 <span
//                   className={`text-sm font-medium ${getTextColor(isDarkMode)}`}
//                 >
//                   {safeFormatDate(companySubscription?.endDate)}
//                 </span>
//               </div>
//             </div>
//           </div>

//           <div className="flex items-center justify-between py-3 border-t border-gray-200 dark:border-gray-700">
//             <div className="flex-1">
//               <div className={`font-medium ${getTextColor(isDarkMode)}`}>
//                 User packs
//               </div>
//               <div className="flex items-center gap-2 mt-2">
//                 <Button
//                   variant="outline"
//                   size="sm"
//                   onClick={decrement}
//                   disabled={
//                     packs <= initialPacks || packs <= metrics.minPacksNeeded
//                   }
//                   className={`${
//                     isDarkMode
//                       ? "border-gray-600 text-white h-8 w-8 p-0"
//                       : "h-8 w-8 p-0"
//                   } disabled:opacity-50 disabled:cursor-not-allowed`}
//                   title={
//                     packs <= metrics.minPacksNeeded
//                       ? `Cannot reduce below ${metrics.minPacksNeeded} packs (${metrics.currentActiveUsers} active members require ${metrics.minPacksNeeded} packs)`
//                       : ""
//                   }
//                 >
//                   <Minus className="w-3 h-3" />
//                 </Button>
//                 <span
//                   className={`px-3 py-1 rounded ${
//                     isDarkMode ? "bg-gray-700" : "bg-gray-100"
//                   } ${getTextColor(isDarkMode)} min-w-[60px] text-center`}
//                 >
//                   {packs}
//                 </span>
//                 <Button
//                   variant="outline"
//                   size="sm"
//                   onClick={increment}
//                   className={
//                     isDarkMode
//                       ? "border-gray-600 text-white h-8 w-8 p-0"
//                       : "h-8 w-8 p-0"
//                   }
//                 >
//                   <Plus className="w-3 h-3" />
//                 </Button>
//                 <span
//                   className={`text-sm ${getTextColor(
//                     isDarkMode,
//                     "secondary"
//                   )} ml-2`}
//                 >
//                   ({10 * metrics.usersPerPack} total users)
//                 </span>
//               </div>
//               {packs <= metrics.minPacksNeeded &&
//                 metrics.currentActiveUsers > 0 && (
//                   <div
//                     className={`mt-2 p-2 rounded text-xs ${
//                       isDarkMode
//                         ? "bg-yellow-900/30 text-yellow-300 border border-yellow-700"
//                         : "bg-yellow-50 text-yellow-800 border border-yellow-200"
//                     }`}
//                   >
//                     ⚠️ You have {metrics.currentActiveUsers} active member
//                     {metrics.currentActiveUsers !== 1 ? "s" : ""} and need at
//                     least {metrics.minPacksNeeded} user pack
//                     {metrics.minPacksNeeded !== 1 ? "s" : ""}. Deactivate
//                     members to reduce packs.
//                   </div>
//                 )}
//             </div>
//             <div className={`font-medium ${getTextColor(isDarkMode)}`}>
//               £{metrics.basePlanCost.toFixed(2)}
//             </div>
//           </div>

//           {metrics.belowCapacity && (
//             <div
//               className={`p-3 rounded-lg border ${
//                 isDarkMode
//                   ? "border-red-600 bg-red-900/20 text-red-300"
//                   : "border-red-300 bg-red-50 text-red-800"
//               }`}
//             >
//               <p className="mb-1 text-sm font-medium">
//                 ⚠️ Insufficient User Capacity
//               </p>
//               <p className="text-sm">
//                 You currently have {metrics.currentActiveUsers} active member
//                 {metrics.currentActiveUsers !== 1 ? "s" : ""} but only{" "}
//                 {metrics.totalCapacity} user slot
//                 {metrics.totalCapacity !== 1 ? "s" : ""}. You need to add at
//                 least {metrics.minPacksNeeded - packs} more user pack
//                 {metrics.minPacksNeeded - packs !== 1 ? "s" : ""} or deactivate{" "}
//                 {metrics.currentActiveUsers - metrics.totalCapacity} member
//                 {metrics.currentActiveUsers - metrics.totalCapacity !== 1
//                   ? "s"
//                   : ""}
//                 .
//               </p>
//             </div>
//           )}
//         </div>

//         {hasChanges && (
//           <div className="flex items-center justify-between pt-4 mt-6 border-t border-gray-200 dark:border-gray-700">
//             <div className={`text-sm ${getTextColor(isDarkMode, "secondary")}`}>
//               You have unsaved changes to your subscription.
//             </div>
//             <Button
//               onClick={() => setShowConfirmDialog(true)}
//               className={`${
//                 isDarkMode
//                   ? "bg-blue-600 hover:bg-blue-700"
//                   : "bg-blue-600 hover:bg-blue-700"
//               } text-white`}
//             >
//               <Check className="w-4 h-4 mr-2" />
//               Confirm Changes
//             </Button>
//           </div>
//         )}

//         <AlertDialog
//           open={showConfirmDialog}
//           onOpenChange={setShowConfirmDialog}
//         >
//           <AlertDialogContent
//             className={isDarkMode ? "bg-gray-800 border-gray-700" : ""}
//           >
//             <AlertDialogHeader>
//               <div className="flex items-center gap-2">
//                 <AlertTriangle
//                   className={`w-5 h-5 ${
//                     isIncrease ? "text-orange-500" : "text-blue-500"
//                   }`}
//                 />
//                 <AlertDialogTitle className={getTextColor(isDarkMode)}>
//                   Confirm Billing Changes
//                 </AlertDialogTitle>
//               </div>
//               <AlertDialogDescription
//                 className={getTextColor(isDarkMode, "secondary")}
//               >
//                 Your subscription billing will change immediately.
//               </AlertDialogDescription>
//             </AlertDialogHeader>

//             <div
//               className={`space-y-4 py-4 ${
//                 isDarkMode ? "text-gray-300" : "text-gray-700"
//               }`}
//             >
//               <div className="space-y-2">
//                 <div className="flex items-center justify-between">
//                   <span className={getTextColor(isDarkMode, "secondary")}>
//                     Current user packs:
//                   </span>
//                   <span className={`font-semibold ${getTextColor(isDarkMode)}`}>
//                     {initialPacks}
//                   </span>
//                 </div>
//                 <div className="flex items-center justify-between">
//                   <span className={getTextColor(isDarkMode, "secondary")}>
//                     New user packs:
//                   </span>
//                   <span className={`font-semibold ${getTextColor(isDarkMode)}`}>
//                     {packs}
//                   </span>
//                 </div>
//               </div>

//               <div
//                 className={`border-t ${
//                   isDarkMode ? "border-gray-700" : "border-gray-200"
//                 } pt-4 space-y-2`}
//               >
//                 <div className="flex items-center justify-between">
//                   <span className={getTextColor(isDarkMode, "secondary")}>
//                     Current monthly cost:
//                   </span>
//                   <span className={getTextColor(isDarkMode)}>
//                     £{oldCost.toFixed(2)}
//                   </span>
//                 </div>
//                 <div className="flex items-center justify-between">
//                   <span className={getTextColor(isDarkMode, "secondary")}>
//                     New monthly cost:
//                   </span>
//                   <span className={`font-semibold ${getTextColor(isDarkMode)}`}>
//                     £{newCost.toFixed(2)}
//                   </span>
//                 </div>
//                 <div
//                   className={`flex justify-between items-center pt-2 border-t ${
//                     isDarkMode ? "border-gray-700" : "border-gray-200"
//                   }`}
//                 >
//                   <span className={`font-semibold ${getTextColor(isDarkMode)}`}>
//                     {isIncrease ? "Additional cost:" : "Savings:"}
//                   </span>
//                   <span
//                     className={`font-bold text-lg ${
//                       isIncrease ? "text-orange-500" : "text-green-500"
//                     }`}
//                   >
//                     {isIncrease ? "+" : "-"}£
//                     {Math.abs(costDifference).toFixed(2)}/month
//                   </span>
//                 </div>
//               </div>

//               <div
//                 className={`${
//                   isDarkMode
//                     ? "bg-blue-900/20 border-blue-600"
//                     : "bg-blue-50 border-blue-200"
//                 } border rounded-lg p-3`}
//               >
//                 <p
//                   className={`text-sm ${
//                     isDarkMode ? "text-blue-200" : "text-blue-800"
//                   }`}
//                 >
//                   💳{" "}
//                   {isIncrease
//                     ? `You will be charged £${Math.abs(costDifference).toFixed(
//                         2
//                       )} pro-rated for the remainder of this billing period.`
//                     : `You will receive a £${Math.abs(costDifference).toFixed(
//                         2
//                       )} credit applied to your next invoice.`}
//                 </p>
//               </div>
//             </div>

//             <AlertDialogFooter>
//               <AlertDialogCancel
//                 className={
//                   isDarkMode
//                     ? "border-gray-600 text-gray-300 hover:bg-gray-700"
//                     : ""
//                 }
//               >
//                 Cancel
//               </AlertDialogCancel>
//               <AlertDialogAction
//                 onClick={async (e) => {
//                   e.preventDefault();
//                   await handleConfirmChanges(packs);
//                 }}
//                 className={`${
//                   isDarkMode
//                     ? "bg-blue-600 hover:bg-blue-700"
//                     : "bg-blue-600 hover:bg-blue-700"
//                 } text-white`}
//                 disabled={updateUserPackLoading}
//               >
//                 {updateUserPackLoading
//                   ? "Please wait..."
//                   : "Confirm & Update Billing"}
//               </AlertDialogAction>
//             </AlertDialogFooter>
//           </AlertDialogContent>
//         </AlertDialog>
//       </div>

//       {/* Usage Summary */}
//       <div className={`${getCardStyle(isDarkMode)} p-6`}>
//         <h4 className={`font-medium ${getTextColor(isDarkMode)} mb-4`}>
//           Current Usage
//         </h4>
//         <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
//           <MetricCard
//             title="Active Users"
//             value={metrics.currentActiveUsers}
//             isDarkMode={isDarkMode}
//           />
//           <MetricCard
//             title="Available Slots"
//             value={metrics.available}
//             isDarkMode={isDarkMode}
//             colorClass={isDarkMode ? "text-green-300" : "text-green-700"}
//           />
//           <MetricCard
//             title="Utilization"
//             value={`${Math.round(
//               (metrics.currentActiveUsers / metrics.totalCapacity) * 100
//             )}%`}
//             isDarkMode={isDarkMode}
//             colorClass={isDarkMode ? "text-blue-300" : "text-blue-700"}
//           />
//         </div>
//       </div>
//     </div>
//   );
// }

function MetricCard({
  title,
  value,
  isDarkMode,
  colorClass,
}: {
  title: string;
  value: number | string;
  isDarkMode: boolean;
  colorClass?: string;
}) {
  const { getTextColor } = useBillingData();

  return (
    <div
      className={`${isDarkMode ? "bg-gray-700" : "bg-gray-50"} rounded-lg p-4`}
    >
      <div className={`${getTextColor(isDarkMode, "secondary")} text-sm`}>
        {title}
      </div>
      <div
        className={`${colorClass ||
          getTextColor(isDarkMode)} text-xl font-semibold`}
      >
        {value}
      </div>
    </div>
  );
}

function InvoicesTable({
  invoices,
  isDarkMode = false,
  currentPage,
  itemsPerPage,
  onPageChange,
}: InvoicesTableProps) {
  const { toast } = useToast();
  const {
    getTextColor,
    getCardStyle,
    getStatusBadgeStyle,
    companyId,
  } = useBillingData();

  const handleDownload = useCallback((invoice: Invoice) => {
    const response = getInvoiceUrl(invoice.invoiceId);
    response.then((result) => {
      if (result?.onlineInvoiceUrl) {
        window.open(result?.onlineInvoiceUrl, "_blank");
      }
    });
  }, []);

  const handleDownloadBillingReport = useCallback(
    async (invoice: Invoice) => {
      if (!companyId) return;

      const invoiceDate = new Date(invoice?.date);
      const hasBillingPeriod =
        invoice?.billingMonth != null && invoice?.billingYear != null;
      if (!hasBillingPeriod && isNaN(invoiceDate.getTime())) return;
      const previousBillingDate = new Date(
        invoiceDate.getFullYear(),
        invoiceDate.getMonth() - 1,
        1,
      );

      const report = await downloadBillingReport({
        month: hasBillingPeriod
          ? invoice.billingMonth
          : previousBillingDate.getMonth() + 1,
        year: hasBillingPeriod
          ? invoice.billingYear
          : previousBillingDate.getFullYear(),
        companyIds: [companyId],
        billinInvoiceType: 2,
        invoiceId: invoice?.id,
      });

      if (!report) return;

      if (!(report instanceof Blob)) {
        const errorMessage =
          "errorMessage" in report ? report.errorMessage : null;
        if (errorMessage) {
          toast({
            title: "Billing report unavailable",
            description: errorMessage,
            variant: "destructive",
          });
        }
        return;
      }

      const blob = report;
      const errorMessage = await getBillingReportErrorMessage(blob);
      if (errorMessage) {
        toast({
          title: "Billing report unavailable",
          description: errorMessage,
          variant: "destructive",
        });
        return;
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${invoice?.reference || "billing-report"}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    [companyId, toast],
  );

  const paginatedInvoices = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return invoices.slice(startIndex, endIndex);
  }, [invoices, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(invoices.length / itemsPerPage);

  return (
    <div className="space-y-4">
      <div className={`${getCardStyle(isDarkMode)} overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={isDarkMode ? "bg-gray-700" : "bg-gray-50"}>
              <tr>
                {[
                  "Invoice",
                  "Date",
                  "Due Date",
                  "Reference",
                  "Total Amount",
                  "Status",
                  "Actions",
                ].map((header) => (
                  <th
                    key={header}
                    className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${getTextColor(
                      isDarkMode,
                      "secondary",
                    )}`}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody
              className={`divide-y ${
                isDarkMode ? "divide-gray-700" : "divide-gray-200"
              }`}
            >
              {paginatedInvoices.map((invoice) => (
                <tr key={invoice?.id}>
                  <td
                    className={`px-4 py-3 text-sm ${getTextColor(isDarkMode)}`}
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      <span className="font-medium">
                        {invoice.invoiceNumber}
                      </span>
                    </div>
                  </td>
                  <td
                    className={`px-4 py-3 text-sm ${getTextColor(isDarkMode)}`}
                  >
                    {format(new Date(invoice?.date), "MMM dd, yyyy")}
                  </td>
                  <td
                    className={`px-4 py-3 text-sm ${getTextColor(isDarkMode)}`}
                  >
                    {format(new Date(invoice?.dueDate), "MMM dd, yyyy")}
                  </td>
                  <td
                    className={`px-4 py-3 text-sm font-medium ${
                      invoice.nhsServicesTotal > 0
                        ? "text-purple-600"
                        : getTextColor(isDarkMode)
                    }`}
                  >
                    {invoice?.reference}
                  </td>
                  <td
                    className={`px-4 py-3 text-sm font-bold ${getTextColor(
                      isDarkMode,
                    )}`}
                  >
                    £{Number(invoice?.amount?.toFixed(3))}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className={getStatusBadgeStyle(invoice.status)}>
                      {invoice.status.charAt(0).toUpperCase() +
                        invoice.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex items-center gap-2">
                      <TooltipProvider delayDuration={150}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownload(invoice)}
                              className={`h-8 px-2 ${
                                isDarkMode
                                  ? "text-gray-300 hover:text-white hover:bg-gray-700"
                                  : "text-gray-600 hover:text-gray-900"
                              }`}
                            >
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent
                            side="left"
                            align="center"
                            className="whitespace-nowrap"
                          >
                            Open Xero Invoice
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <TooltipProvider delayDuration={150}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                handleDownloadBillingReport(invoice)
                              }
                              disabled={
                                invoice?.reference?.slice(0, 3) !== "SVC" ||
                                !companyId ||
                                new Date(invoice?.date) < new Date("2026-07-01")
                              }
                              className={`h-8 px-2 ${
                                isDarkMode
                                  ? "text-gray-300 hover:text-white hover:bg-gray-700"
                                  : "text-gray-600 hover:text-gray-900"
                              } disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent
                            side="left"
                            align="center"
                            className="whitespace-nowrap"
                          >
                            Invoice Breakdown
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {invoices.length > 0 && (
        <TablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={invoices.length}
          itemsPerPage={itemsPerPage}
          onPageChange={onPageChange}
          isDarkMode={isDarkMode}
          className={`${getCardStyle(isDarkMode)} p-4`}
        />
      )}
    </div>
  );
}
